import { confirmReport, rejectReport, Report } from '~/api/reports'
import { useReports } from './useReports'
import { getAbilityPromise } from '~/defineAbility'

/** @type { import('@casl/ability').Ability } */
let ability

getAbilityPromise.then((_ability) => ability = _ability)

/** @enum */
const Status = {
	0: 'text-neutral-90',
	[Report.Confirmed]: 'text-primary',
	[Report.Rejected]: 'text-red-500',
}

/**
 * @typedef ItemReportData
 * @property { boolean } loading
 * @memberof ItemReport
 */

/**
 * @typedef ItemReportComputed
 * @property { boolean } notResolved
 * @property { boolean } showActions
 */

/**
 * @typedef ItemReportMethods
 * @property { () => void } reject
 * @property { () => void } confirm
 */

/**
 * @typedef ItemReportProps
 * @property { import('~/api/reports').Report } report
 */

/** @type { import('vue/types/options').ThisTypedComponentOptionsWithRecordProps<{}, ItemReportData, ItemReportMethods, ItemReportComputed, ItemReportProps> } */
const ItemReport = {
	props: {
		report: Object,
	},
	data: () => ({
		loading: false,
	}),
	computed: {
		notResolved: function() {
			return !this.report[Report.Confirmed] && !this.report[Report.Rejected]
		},
		showActions: function() {
			return this.report[Report.CanUpdate] && ability.can('confirm', 'Reports') && this.notResolved
		},
	},
	methods: {
		reject() {
			this.loading = true

			rejectReport(this.report.ID).then((data) => {
				this.report.REJECTED = true
				this.loading = false
				useReports().mutate()
			})
		},
		confirm() {
			this.loading = true

			confirmReport(this.report.ID).then((data) => {
				this.report.CONFIRMED = true
				this.loading = false
				useReports().mutate()
			})
		},
	},
	render(h) {
		return (
			<div class="grid grid-cols-2">
				<span
					class={{
						'u-typo-heading3 mr-8': true,
						[Status[0]]: this.notResolved,
						[Status[Report.Confirmed]]: this.report[Report.Confirmed],
						[Status[Report.Rejected]]: this.report[Report.Rejected],
					}}
				>
					Отчет #{ this.report[Report.Name] }
				</span>
				<span>
					<a
						href={`/api/Reports/get/?format=xls&id=${this.report[Report.Id]}`}
						class="button button_theme_secondary button_size_s mr-6"
					>
						XLS
					</a>
					<a
						href={`/api/Reports/get/?format=pdf&id=${this.report[Report.Id]}`}
						class="button button_theme_secondary button_size_s"
					>
						PDF
					</a>
					{ this.showActions ? (
						<button
							type="button"
							class="ml-8 button button_theme_secondary-ghost button_size_s"
							onClick={ this.confirm }
							disabled={ this.loading }
						>
							Подтвердить
						</button>
					) : null }
					{ this.showActions ? (
						<button
							type="button"
							class="ml-8 button button_theme_secondary-ghost button_size_s"
							onClick={ this.reject }
							disabled={ this.loading }
						>
							Отклонить
						</button>
					) : null }
				</span>
			</div>
		)
	}
}

/** @type { import('vue').FunctionalComponentOptions } */
const Legend = {
	functional: true,
	render(h) {
		return (
			<ul class="list-unstyled">
				<li>
					<span class={ Status[0] }>&#x2B24;&#xFE0E;</span> — отчет, который нужно утвердить.
				</li>
				<li>
					<span class={ Status[Report.Confirmed] }>&#x2B24;&#xFE0E;</span> — распечатайте данный отчет, подпишите, и пришлите к нам на наш почтовый адрес.
				</li>
				<li>
					<span class={ Status[Report.Rejected] }>&#x2B24;&#xFE0E;</span> — данный отчет вы отклонили или прошло время на его подтверждение, вам нужно связаться с нами через Техподдержку.
				</li>
			</ul>
		)
	},
}

/** @type { import('vue/types/options').ThisTypedComponentOptionsWithRecordProps<{}, {}, {}, { reports: import('~/api/reports').Report[] }, {}> } */
export const ListReports = {
	computed: {
		reports: () => useReports().data,
	},
	mounted() {
		useReports().mutate();
	},
	render(h) {
		return (
			<div>
				<Legend />
				<hr />
				<ul
					class="list-unstyled grid gap-y-8 m-0"
					style={{
						'max-width': 'max-content',
					}}
				>
					{ this.reports.map((report) => (
						<li>
							<ItemReport report={ report } />
						</li>
					)) }
				</ul>
			</div>
		)
	},
}

export default ListReports
